import React, { useEffect, useState, useRef, Children, lazy } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { RecentPurchaseToast } from '../components/RecentPurchaseToast';
import { initMetaPixel, trackAddToCart } from '../utils/metaPixel';
import { DollarSignIcon, FlaskConicalIcon, FlameIcon, ClockIcon, StarIcon, MicroscopeIcon, ShieldCheckIcon, SparklesIcon, BookOpenIcon, GlobeIcon, DropletsIcon, DumbbellIcon, LockIcon, ZapIcon, CheckCircle2Icon, ArrowRightIcon, MenuIcon, XIcon, AlertTriangleIcon, TargetIcon, CheckIcon, BanIcon, CalendarIcon, CameraIcon, CheckSquareIcon, FileTextIcon, PrinterIcon, BellIcon, SmartphoneIcon } from 'lucide-react';
import * as THREE from 'three';
// ─── TYPES ───
interface BonusItem {
  icon: React.ReactNode;
  title: string;
  desc: string;
  image?: string;
}
interface TestimonialData {
  name: string;
  city: string;
  quote: string;
  initials: string;
}
interface ProblemCard {
  icon: React.ReactNode;
  title: string;
  desc: string;
}
interface CycleStep {
  num: number;
  title: string;
  desc: string;
  color: string;
}
// ─── CONSTANTS ───
const GOLD = '#c9a96e';
const GOLD_LIGHT = '#e8d5a3';
const GOLD_DARK = '#8b6914';
const CREAM = '#f5f0e8';
const BG = '#0a0608';
const GLASS_BG = 'rgba(255,255,255,0.04)';
const GLASS_BORDER = 'rgba(201,169,110,0.15)';
const BONUSES: BonusItem[] = [{
  icon: <BookOpenIcon className="w-5 h-5" style={{
    color: GOLD
  }} data-id="element-289" />,
  title: 'Protocolo Principal',
  desc: 'El sistema completo de Skin-Cycling con genéricos',
  image: 'https://cdn.magicpatterns.com/uploads/xttax9sDv9asFvsKmFZEPb/mockup-1.png'
}, {
  icon: <DollarSignIcon className="w-5 h-5" style={{
    color: GOLD
  }} data-id="element-290" />,
  title: 'Matriz de Dupes 2026',
  desc: 'Equivalentes genéricos de las marcas más caras',
  image: 'https://cdn.magicpatterns.com/uploads/oBDeEMsQHuJ2SNrwjf7Vn6/bn-1.png'
}, {
  icon: <GlobeIcon className="w-5 h-5" style={{
    color: GOLD
  }} data-id="element-291" />,
  title: 'Directorio de Farmacias',
  desc: 'Dónde comprar cada producto al mejor precio',
  image: 'https://cdn.magicpatterns.com/uploads/wKjBeGo43YdQqT2SRa1vve/bn-2.png'
}, {
  icon: <DropletsIcon className="w-5 h-5" style={{
    color: GOLD
  }} data-id="element-292" />,
  title: 'Guía Cuello y Manos',
  desc: 'Protocolo anti-edad para las zonas olvidadas',
  image: 'https://cdn.magicpatterns.com/uploads/q6eVvcHZ3NG6CEZ2pcZZyA/bn-3.png'
}, {
  icon: <FlaskConicalIcon className="w-5 h-5" style={{
    color: GOLD
  }} data-id="element-293" />,
  title: 'Mapa de pH',
  desc: 'Ciencia del pH para maximizar cada activo',
  image: 'https://cdn.magicpatterns.com/uploads/fVTyXNK2mibNNJyVPv6oJT/bn-5.png'
}, {
  icon: <DumbbellIcon className="w-5 h-5" style={{
    color: GOLD
  }} data-id="element-294" />,
  title: 'Protocolo Gym-Skin',
  desc: 'Skincare optimizado para mujeres activas',
  image: 'https://cdn.magicpatterns.com/uploads/ikPUo6HhtqfAquJftM7BGJ/bn-4.png'
}];
const PROBLEMS: ProblemCard[] = [{
  icon: <DollarSignIcon className="w-6 h-6" data-id="element-295" />,
  title: 'Pagas el envase, no la molécula',
  desc: 'Las marcas premium cobran 10x más por los mismos activos que encuentras en genéricos de farmacia.'
}, {
  icon: <FlaskConicalIcon className="w-6 h-6" data-id="element-296" />,
  title: 'Mezclas activos que se anulan',
  desc: 'Vitamina C con niacinamida, retinol con AHA... combinaciones que destruyen la eficacia.'
}, {
  icon: <FlameIcon className="w-6 h-6" data-id="element-297" />,
  title: '"Si arde es que funciona" — Mito',
  desc: 'La irritación no es señal de eficacia. Es señal de que tu barrera cutánea está comprometida.'
}, {
  icon: <ClockIcon className="w-6 h-6" data-id="element-298" />,
  title: 'Rutinas de 12 pasos imposibles',
  desc: 'No necesitas 12 productos. Necesitas los 4 correctos, en el orden correcto, en el día correcto.'
}];
const CYCLE_STEPS: CycleStep[] = [{
  num: 1,
  title: 'Exfoliación',
  desc: 'AHA/BHA eliminan células muertas y destapan poros',
  color: '#c9a96e'
}, {
  num: 2,
  title: 'Retinización',
  desc: 'Retinol estimula colágeno y renueva la piel',
  color: '#e8d5a3'
}, {
  num: 3,
  title: 'Recuperación',
  desc: 'Ceramidas y péptidos reparan la barrera',
  color: '#a89070'
}, {
  num: 4,
  title: 'Recuperación',
  desc: 'Hidratación profunda y protección solar',
  color: '#8b6914'
}];
const TESTIMONIALS: TestimonialData[] = [{
  name: 'Sofía R.',
  city: 'CDMX',
  quote: 'En 2 semanas mi piel se ve más luminosa que con cremas de $2,000. No puedo creer que estos genéricos funcionen tan bien.',
  initials: 'SR'
}, {
  name: 'Valentina M.',
  city: 'Guadalajara',
  quote: 'Mi dermatóloga me preguntó qué estaba usando. Cuando le dije que eran genéricos de farmacia, no lo podía creer.',
  initials: 'VM'
}, {
  name: 'Carmen L.',
  city: 'Monterrey',
  quote: 'Gasté miles en marcas premium. Este protocolo con genéricos me dio mejores resultados en la mitad del tiempo.',
  initials: 'CL'
}];
const MARQUEE_ITEMS = [{
  icon: <SparklesIcon className="w-4 h-4" data-id="element-299" />,
  text: '+3,200 mujeres'
}, {
  icon: <MicroscopeIcon className="w-4 h-4" data-id="element-300" />,
  text: 'Ciencia dermatológica'
}, {
  icon: <CheckCircle2Icon className="w-4 h-4" data-id="element-301" />,
  text: 'Genéricos comprobados'
}, {
  icon: <ShieldCheckIcon className="w-4 h-4" data-id="element-302" />,
  text: 'Garantía 7 días'
}, {
  icon: <ZapIcon className="w-4 h-4" data-id="element-303" />,
  text: 'Acceso inmediato'
}, {
  icon: <StarIcon className="w-4 h-4" data-id="element-304" />,
  text: 'Resultados semana 1'
}];
// ─── ANIMATION VARIANTS ───
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12
    }
  }
};
const slideInRight = {
  hidden: {
    opacity: 0,
    x: 60
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};
// ─── GLASS CARD COMPONENT ───
function GlassCard({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`rounded-2xl border p-6 ${className}`} style={{
    background: GLASS_BG,
    borderColor: GLASS_BORDER,
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)'
  }} data-id="element-305">
      {children}
    </div>;
}
// ─── SECTION LABEL ───
function SectionLabel({
  icon,
  text
}: {
  icon: React.ReactNode;
  text: string;
}) {
  return <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-50px'
  }} className="flex items-center gap-2 mb-4" data-id="element-306">
      <span style={{
      color: GOLD
    }} data-id="element-307">
        {icon}
      </span>
      <span className="text-xs font-semibold tracking-[0.2em] uppercase" style={{
      color: GOLD
    }} data-id="element-308">
        {text}
      </span>
    </motion.div>;
}
// ─── MAIN COMPONENT ───
export function LujoPage() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [cursorPos, setCursorPos] = useState({
    x: 0,
    y: 0
  });
  const [followerPos, setFollowerPos] = useState({
    x: 0,
    y: 0
  });
  const [isMobile, setIsMobile] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [bump1, setBump1] = useState(false);
  const [bump2, setBump2] = useState(false);
  const BASE_PRICE = 11.99;
  const BUMP1_PRICE = 10.99;
  const BUMP2_PRICE = 9.99;
  const totalPrice = BASE_PRICE + (bump1 ? BUMP1_PRICE : 0) + (bump2 ? BUMP2_PRICE : 0);
  const [showUpsell, setShowUpsell] = useState(false);
  const FULL_BUNDLE_URL = 'https://rutinasimple.com//cart/52235919327513:1';
  const BASE_ONLY_URL = 'https://rutinasimple.com//cart/52219237794073:1';
  const BUNDLE_PRICE = 26.68; // $11.99 + ($20.98 × 0.70)
  const getCheckoutUrl = () => {
    if (bump1 && bump2) return FULL_BUNDLE_URL;
    if (bump1) return 'https://rutinasimple.com//cart/52235856970009:1';
    if (bump2) return 'https://rutinasimple.com//cart/52235858837785:1';
    return BASE_ONLY_URL;
  };
  const handleCheckout = () => {
    if (!bump1 && !bump2) {
      setShowUpsell(true);
      return;
    }
    trackAddToCart();
    window.open(getCheckoutUrl(), '_blank');
  };
  const mouseRef = useRef({
    x: 0,
    y: 0
  });
  const {
    scrollYProgress
  } = useScroll();
  const heroOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0]);
  // Meta Pixel
  useEffect(() => {
    initMetaPixel();
  }, []);
  // Mobile detection
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  // Custom cursor
  useEffect(() => {
    if (isMobile) return;
    const handleMove = (e: MouseEvent) => {
      setCursorPos({
        x: e.clientX,
        y: e.clientY
      });
      mouseRef.current = {
        x: e.clientX,
        y: e.clientY
      };
    };
    window.addEventListener('mousemove', handleMove);
    let raf: number;
    const follow = () => {
      setFollowerPos(prev => ({
        x: prev.x + (mouseRef.current.x - prev.x) * 0.12,
        y: prev.y + (mouseRef.current.y - prev.y) * 0.12
      }));
      raf = requestAnimationFrame(follow);
    };
    raf = requestAnimationFrame(follow);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      cancelAnimationFrame(raf);
    };
  }, [isMobile]);
  // Three.js particles
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    camera.position.z = 4;
    const renderer = new THREE.WebGLRenderer({
      canvas,
      alpha: true,
      antialias: true
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    const particleCount = isMobile ? 400 : 1500;
    const positions = new Float32Array(particleCount * 3);
    const colors = new Float32Array(particleCount * 3);
    const goldColor = new THREE.Color(GOLD);
    const creamColor = new THREE.Color(CREAM);
    const roseColor = new THREE.Color('#d4a0a0');
    for (let i = 0; i < particleCount; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const r = 2 + Math.random() * 1.5;
      positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = r * Math.cos(phi);
      const colorChoice = [goldColor, creamColor, roseColor][Math.floor(Math.random() * 3)];
      colors[i * 3] = colorChoice.r;
      colors[i * 3 + 1] = colorChoice.g;
      colors[i * 3 + 2] = colorChoice.b;
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    const material = new THREE.PointsMaterial({
      size: 0.015,
      vertexColors: true,
      transparent: true,
      opacity: 0.7,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    });
    const points = new THREE.Points(geometry, material);
    scene.add(points);
    // Wireframe icosahedron
    const icoGeo = new THREE.IcosahedronGeometry(2, 1);
    const icoMat = new THREE.MeshBasicMaterial({
      color: GOLD,
      wireframe: true,
      transparent: true,
      opacity: 0.06
    });
    const ico = new THREE.Mesh(icoGeo, icoMat);
    scene.add(ico);
    const mouse = {
      x: 0,
      y: 0
    };
    const handleMouse = (e: MouseEvent) => {
      mouse.x = (e.clientX / window.innerWidth - 0.5) * 2;
      mouse.y = -(e.clientY / window.innerHeight - 0.5) * 2;
    };
    window.addEventListener('mousemove', handleMouse);
    let scrollY = 0;
    const handleScroll = () => {
      scrollY = window.scrollY;
    };
    window.addEventListener('scroll', handleScroll);
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    let animId: number;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      points.rotation.y += 0.001;
      points.rotation.x += 0.0005;
      ico.rotation.y += 0.002;
      ico.rotation.x += 0.001;
      // Mouse parallax
      camera.position.x += (mouse.x * 0.3 - camera.position.x) * 0.05;
      camera.position.y += (mouse.y * 0.3 - camera.position.y) * 0.05;
      // Scroll fade
      const scrollFade = Math.max(0, 1 - scrollY / (window.innerHeight * 0.8));
      material.opacity = 0.7 * scrollFade;
      icoMat.opacity = 0.06 * scrollFade;
      camera.position.z = 4 + scrollY * 0.001;
      camera.lookAt(scene.position);
      renderer.render(scene, camera);
    };
    animate();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('mousemove', handleMouse);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      renderer.dispose();
      geometry.dispose();
      material.dispose();
      icoGeo.dispose();
      icoMat.dispose();
    };
  }, [isMobile]);
  const scrollToOffer = () => {
    document.getElementById('lujo-oferta')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="relative w-full min-h-screen overflow-x-hidden" style={{
    background: BG,
    color: CREAM,
    fontFamily: "'DM Sans', sans-serif",
    cursor: isMobile ? 'auto' : 'none'
  }} data-id="element-309">
      {/* Injected keyframe styles */}
      <style data-id="element-310">{`
        @keyframes lujo-marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes lujo-seal-rotate {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes lujo-pulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
        .lujo-marquee-track {
          animation: lujo-marquee 25s linear infinite;
        }
        .lujo-seal {
          animation: lujo-seal-rotate 20s linear infinite;
        }
        .lujo-pulse {
          animation: lujo-pulse 2s ease-in-out infinite;
        }
      `}</style>

      {/* Custom Cursor */}
      {!isMobile && <>
          <div className="fixed top-0 left-0 pointer-events-none z-[9999] rounded-full" style={{
        width: 8,
        height: 8,
        background: GOLD,
        transform: `translate(${cursorPos.x - 4}px, ${cursorPos.y - 4}px)`,
        mixBlendMode: 'difference'
      }} data-id="element-311" />
          <div className="fixed top-0 left-0 pointer-events-none z-[9998] rounded-full border" style={{
        width: 36,
        height: 36,
        borderColor: GOLD,
        transform: `translate(${followerPos.x - 18}px, ${followerPos.y - 18}px)`,
        mixBlendMode: 'difference',
        opacity: 0.5
      }} data-id="element-312" />
        </>}

      {/* Three.js Canvas */}
      <canvas ref={canvasRef} className="fixed inset-0 w-full h-full pointer-events-none z-0" data-id="element-313" />

      {/* ─── NAVBAR ─── */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b" style={{
      background: 'rgba(10,6,8,0.85)',
      backdropFilter: 'blur(16px)',
      borderColor: GLASS_BORDER
    }} data-id="element-314">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-315">
          <span className="text-lg font-bold tracking-wider" style={{
          fontFamily: "'Playfair Display', serif",
          color: GOLD
        }} data-id="element-316">
            PIEL DE LUJO
          </span>
          <div className="hidden md:flex items-center gap-6" data-id="element-317">
            <a href="#lujo-problema" className="text-sm hover:opacity-80 transition-opacity" style={{
            color: CREAM
          }} data-id="element-318">
              Problema
            </a>
            <a href="#lujo-mecanismo" className="text-sm hover:opacity-80 transition-opacity" style={{
            color: CREAM
          }} data-id="element-319">
              Método
            </a>
            <a href="#lujo-oferta" className="text-sm hover:opacity-80 transition-opacity" style={{
            color: CREAM
          }} data-id="element-320">
              Oferta
            </a>
            <button onClick={scrollToOffer} className="px-5 py-2 text-sm font-semibold transition-all hover:scale-105" style={{
            background: GOLD,
            color: BG,
            cursor: isMobile ? 'pointer' : 'none'
          }} data-id="element-321">
              OBTENER ACCESO
            </button>
          </div>
          <button className="md:hidden" onClick={() => setMenuOpen(!menuOpen)} style={{
          color: CREAM
        }} data-id="element-322">
            {menuOpen ? <XIcon className="w-6 h-6" data-id="element-323" /> : <MenuIcon className="w-6 h-6" data-id="element-324" />}
          </button>
        </div>
        {menuOpen && <div className="md:hidden px-4 pb-4 flex flex-col gap-3" style={{
        background: 'rgba(10,6,8,0.95)'
      }} data-id="element-325">
            <a href="#lujo-problema" className="text-sm py-2" style={{
          color: CREAM
        }} onClick={() => setMenuOpen(false)} data-id="element-326">
              Problema
            </a>
            <a href="#lujo-mecanismo" className="text-sm py-2" style={{
          color: CREAM
        }} onClick={() => setMenuOpen(false)} data-id="element-327">
              Método
            </a>
            <a href="#lujo-oferta" className="text-sm py-2" style={{
          color: CREAM
        }} onClick={() => setMenuOpen(false)} data-id="element-328">
              Oferta
            </a>
            <button onClick={() => {
          setMenuOpen(false);
          scrollToOffer();
        }} className="px-5 py-2 text-sm font-semibold w-full" style={{
          background: GOLD,
          color: BG
        }} data-id="element-329">
              OBTENER ACCESO
            </button>
          </div>}
      </nav>

      {/* ─── HERO ─── */}
      <motion.section style={{
      opacity: heroOpacity
    }} className="relative z-10 pt-28 md:pt-36 pb-16 md:pb-24 px-4" data-id="element-330">
        <div className="max-w-4xl mx-auto text-center" data-id="element-331">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5
        }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-8" style={{
          borderColor: GLASS_BORDER,
          background: GLASS_BG
        }} data-id="element-332">
            <span className="lujo-pulse" data-id="element-333">
              <SparklesIcon className="w-4 h-4" style={{
              color: GOLD
            }} data-id="element-334" />
            </span>
            <span className="text-xs tracking-wider uppercase" style={{
            color: GOLD_LIGHT
          }} data-id="element-335">
              Protocolo Dermatológico 2026
            </span>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.15
        }} className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6" style={{
          fontFamily: "'Playfair Display', serif",
          color: CREAM
        }} data-id="element-336">
            Piel de Porcelana en{' '}
            <span style={{
            background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }} data-id="element-337">
              28 Días
            </span>{' '}
            con Genéricos
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="text-base md:text-lg max-w-2xl mx-auto mb-10 leading-relaxed" style={{
          color: 'rgba(245,240,232,0.7)'
        }} data-id="element-338">
            El mismo protocolo de Skin-Cycling que usan las dermatólogas de
            Beverly Hills, adaptado a productos de farmacia que cuestan menos de
            $200 MXN.
          </motion.p>

          {/* VSL Embed */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.4
        }} className="max-w-2xl mx-auto mb-10 rounded-xl overflow-hidden border" style={{
          borderColor: GLASS_BORDER
        }} data-id="element-339">
            <div style={{
            position: 'relative',
            paddingTop: '56.25%'
          }} data-id="element-340">
              <iframe src="https://embed.fliki.ai/embed/69f7e23c95fbcb8ada2c035d" loading="lazy" style={{
              border: 0,
              position: 'absolute',
              top: 0,
              height: '100%',
              width: '100%'
            }} allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;" allowFullScreen data-id="element-341" />
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.45
        }} className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-342">
            <button onClick={scrollToOffer} className="px-8 py-4 text-base font-semibold tracking-wider flex items-center justify-center gap-2 transition-all hover:scale-105" style={{
            background: GOLD,
            color: BG,
            cursor: isMobile ? 'pointer' : 'none'
          }} data-id="element-343">
              QUIERO MI PROTOCOLO
              <ArrowRightIcon className="w-5 h-5" data-id="element-344" />
            </button>
            <button onClick={scrollToOffer} className="px-8 py-4 text-base font-semibold tracking-wider border transition-all hover:scale-105" style={{
            borderColor: GLASS_BORDER,
            color: CREAM,
            background: 'transparent',
            cursor: isMobile ? 'pointer' : 'none'
          }} data-id="element-345">
              VER CONTENIDO
            </button>
          </motion.div>
        </div>
      </motion.section>

      {/* ─── SOCIAL PROOF MARQUEE ─── */}
      <div className="relative z-10 border-y overflow-hidden py-3" style={{
      borderColor: GLASS_BORDER,
      background: 'rgba(201,169,110,0.04)'
    }} data-id="element-346">
        <div className="lujo-marquee-track flex items-center gap-8 whitespace-nowrap" style={{
        width: 'max-content'
      }} data-id="element-347">
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS, ...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => <span key={i} className="flex items-center gap-2 text-sm" style={{
          color: GOLD_LIGHT
        }} data-id="element-348">
              {item.icon}
              {item.text}
            </span>)}
        </div>
      </div>

      {/* ─── PROBLEMA ─── */}
      <section id="lujo-problema" className="relative z-10 py-16 md:py-24 px-4" data-id="element-349">
        <div className="max-w-5xl mx-auto" data-id="element-350">
          <SectionLabel icon={<FlameIcon className="w-4 h-4" data-id="element-352" />} text="El Problema" data-id="element-351" />
          <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="text-3xl md:text-5xl font-bold mb-4" style={{
          fontFamily: "'Playfair Display', serif"
        }} data-id="element-353">
            La industria del skincare te está{' '}
            <span style={{
            color: GOLD
          }} data-id="element-354">
              robando
            </span>
          </motion.h2>
          <motion.p variants={fadeUp} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="text-base md:text-lg mb-12 max-w-2xl" style={{
          color: 'rgba(245,240,232,0.6)'
        }} data-id="element-355">
            Cada año gastas miles en productos que no necesitas. Aquí está la
            verdad que nadie te cuenta.
          </motion.p>

          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-356">
            {PROBLEMS.map((p, i) => <motion.div key={i} variants={fadeUp} data-id="element-357">
                <GlassCard className="h-full hover:border-opacity-40 transition-all duration-300" data-id="element-358">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4" style={{
                background: 'rgba(201,169,110,0.1)',
                color: GOLD
              }} data-id="element-359">
                    {p.icon}
                  </div>
                  <h3 className="text-lg font-bold mb-2" style={{
                fontFamily: "'Playfair Display', serif",
                color: CREAM
              }} data-id="element-360">
                    {p.title}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{
                color: 'rgba(245,240,232,0.6)'
              }} data-id="element-361">
                    {p.desc}
                  </p>
                </GlassCard>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ─── MECANISMO ─── */}
      <section id="lujo-mecanismo" className="relative z-10 py-16 md:py-24 px-4" data-id="element-362">
        <div className="max-w-5xl mx-auto" data-id="element-363">
          <SectionLabel icon={<MicroscopeIcon className="w-4 h-4" data-id="element-365" />} text="El Método" data-id="element-364" />
          <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="text-3xl md:text-5xl font-bold mb-4" style={{
          fontFamily: "'Playfair Display', serif"
        }} data-id="element-366">
            Skin-Cycling:{' '}
            <span style={{
            color: GOLD
          }} data-id="element-367">
              4 noches, 1 ciclo
            </span>
          </motion.h2>
          <motion.p variants={fadeUp} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="text-base md:text-lg mb-12 max-w-2xl" style={{
          color: 'rgba(245,240,232,0.6)'
        }} data-id="element-368">
            El sistema respaldado por dermatólogos que alterna activos potentes
            con noches de recuperación.
          </motion.p>

          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} className="grid grid-cols-2 md:grid-cols-4 gap-4 relative" data-id="element-369">
            {/* Connecting line (desktop) */}
            <div className="hidden md:block absolute top-12 left-[12%] right-[12%] h-px" style={{
            background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
            opacity: 0.3
          }} data-id="element-370" />
            {CYCLE_STEPS.map((step, i) => <motion.div key={i} variants={fadeUp} className="text-center relative" data-id="element-371">
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-full border-2 flex items-center justify-center mx-auto mb-4 text-xl font-bold relative z-10" style={{
              borderColor: step.color,
              color: step.color,
              background: BG,
              fontFamily: "'Playfair Display', serif"
            }} data-id="element-372">
                  {step.num}
                </div>
                <h3 className="text-sm md:text-base font-bold mb-1" style={{
              fontFamily: "'Playfair Display', serif",
              color: CREAM
            }} data-id="element-373">
                  {step.title}
                </h3>
                <p className="text-xs md:text-sm leading-relaxed" style={{
              color: 'rgba(245,240,232,0.5)'
            }} data-id="element-374">
                  {step.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ─── OFERTA ─── */}
      <section id="lujo-oferta" className="relative z-10 py-16 md:py-24 px-4" data-id="element-375">
        <div className="max-w-5xl mx-auto" data-id="element-376">
          <SectionLabel icon={<SparklesIcon className="w-4 h-4" data-id="element-378" />} text="Tu Acceso" data-id="element-377" />
          <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="text-3xl md:text-5xl font-bold mb-12" style={{
          fontFamily: "'Playfair Display', serif"
        }} data-id="element-379">
            Todo lo que recibes{' '}
            <span style={{
            color: GOLD
          }} data-id="element-380">
              hoy
            </span>
          </motion.h2>

          {/* Price Card */}
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} data-id="element-381">
            <GlassCard className="max-w-2xl mx-auto mb-12 text-center" data-id="element-382">
              <div className="flex justify-center mb-6" data-id="element-383">
                <img src="https://cdn.magicpatterns.com/uploads/xttax9sDv9asFvsKmFZEPb/mockup-1.png" alt="El Protocolo de la Piel de Lujo" className="w-48 md:w-56 object-contain drop-shadow-2xl" loading="lazy" decoding="async" width={224} height={280} data-id="element-384" />
              </div>
              <p className="text-sm tracking-wider uppercase mb-2" style={{
              color: GOLD
            }} data-id="element-385">
                Valor total del paquete
              </p>
              <div className="flex items-center justify-center gap-4 mb-4" data-id="element-386">
                <span className="text-2xl line-through" style={{
                color: 'rgba(245,240,232,0.3)'
              }} data-id="element-387">
                  $452
                </span>
                <span className="text-5xl md:text-6xl font-bold" style={{
                fontFamily: "'Playfair Display', serif",
                background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }} data-id="element-388">
                  <motion.span key={totalPrice} initial={{
                  scale: 1.1
                }} animate={{
                  scale: 1
                }} data-id="element-389">
                    ${totalPrice.toFixed(2)}
                  </motion.span>
                </span>
              </div>
              <p className="text-sm mb-6" style={{
              color: 'rgba(245,240,232,0.5)'
            }} data-id="element-390">
                Pago único — Acceso de por vida — Sin suscripciones
              </p>

              {/* Bonus list */}
              <div className="text-left space-y-3 mb-8" data-id="element-391">
                {BONUSES.map((b, i) => <div key={i} className="flex items-start gap-3" data-id="element-392">
                    <CheckCircle2Icon className="w-5 h-5 mt-0.5 flex-shrink-0" style={{
                  color: GOLD
                }} data-id="element-393" />
                    <div data-id="element-394">
                      <span className="text-sm font-semibold" style={{
                    color: CREAM
                  }} data-id="element-395">
                        {b.title}
                      </span>
                      <span className="text-sm ml-1" style={{
                    color: 'rgba(245,240,232,0.5)'
                  }} data-id="element-396">
                        — {b.desc}
                      </span>
                    </div>
                  </div>)}
              </div>

              <button onClick={handleCheckout} className="w-full py-4 text-base font-semibold tracking-wider flex items-center justify-center gap-2 transition-all hover:scale-[1.02]" style={{
              background: GOLD,
              color: BG,
              cursor: isMobile ? 'pointer' : 'none'
            }} data-id="element-397">
                OBTENER ACCESO AHORA
                <ArrowRightIcon className="w-5 h-5" data-id="element-398" />
              </button>

              <div className="flex items-center justify-center gap-4 mt-4 text-xs" style={{
              color: 'rgba(245,240,232,0.4)'
            }} data-id="element-399">
                <span className="flex items-center gap-1" data-id="element-400">
                  <LockIcon className="w-3 h-3" data-id="element-401" /> Pago seguro
                </span>
                <span className="flex items-center gap-1" data-id="element-402">
                  <ShieldCheckIcon className="w-3 h-3" data-id="element-403" /> Garantía 7 días
                </span>
                <span className="flex items-center gap-1" data-id="element-404">
                  <ZapIcon className="w-3 h-3" data-id="element-405" /> Acceso inmediato
                </span>
              </div>
            </GlassCard>

            {/* ─── ORDER BUMP #1 — LA BIBLIA DE LAS MEZCLAS ─── */}
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="max-w-2xl mx-auto mb-4" data-id="element-406">
              <div className="rounded-2xl overflow-hidden transition-all duration-300" style={{
              background: '#13100f',
              boxShadow: bump1 ? `0 0 0 2px ${GOLD}, 0 20px 60px rgba(201,169,110,0.2)` : 'none'
            }} data-id="element-407">
                {/* Ribbon */}
                <div className="px-5 py-2 flex items-center justify-between text-xs font-bold tracking-wider uppercase" style={{
                background: `linear-gradient(90deg, ${GOLD_DARK}, ${GOLD}, ${GOLD_DARK})`,
                color: BG
              }} data-id="element-408">
                  <span className="flex items-center gap-1.5" data-id="element-409">
                    <AlertTriangleIcon className="w-3.5 h-3.5" data-id="element-410" /> ALTAMENTE
                    RECOMENDADA
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-[10px]" style={{
                  background: 'rgba(0,0,0,0.2)'
                }} data-id="element-411">
                    MÁS POPULAR
                  </span>
                </div>

                <div className="p-5 md:p-7" data-id="element-412">
                  {/* Top: Checkbox + Headline + Image */}
                  <div className="flex gap-4 items-start mb-5 pb-5 border-b" style={{
                  borderColor: GLASS_BORDER
                }} data-id="element-413">
                    <button onClick={() => setBump1(!bump1)} className="flex-shrink-0 mt-1 w-7 h-7 rounded-md border-2 flex items-center justify-center transition-all" style={{
                    borderColor: GOLD,
                    background: bump1 ? GOLD : 'transparent',
                    cursor: isMobile ? 'pointer' : 'none'
                  }} data-id="element-414">
                      {bump1 && <CheckIcon className="w-4 h-4" style={{
                      color: BG
                    }} data-id="element-415" />}
                    </button>
                    <div className="flex-1" data-id="element-416">
                      <p className="text-[11px] font-bold tracking-wider uppercase mb-1 flex items-center gap-1" style={{
                      color: '#e88888'
                    }} data-id="element-417">
                        <AlertTriangleIcon className="w-3 h-3" data-id="element-418" /> Espera — Antes
                        de continuar
                      </p>
                      <h3 className="text-base md:text-lg font-bold mb-2 leading-tight" style={{
                      fontFamily: "'Playfair Display', serif",
                      color: CREAM
                    }} data-id="element-419">
                        ¿Sabes qué ingredientes de tu protocolo{' '}
                        <span style={{
                        background: `linear-gradient(135deg, ${GOLD_LIGHT}, ${GOLD})`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }} data-id="element-420">
                          NUNCA debes mezclar?
                        </span>
                      </h3>
                      <p className="text-sm leading-relaxed" style={{
                      color: 'rgba(245,240,232,0.6)'
                    }} data-id="element-421">
                        El 73% de las mujeres que siguen un protocolo de
                        Skin-Cycling lo arruinan sin saberlo por combinar
                        activos que se destruyen entre sí.
                      </p>
                    </div>
                    <img src="https://cdn.magicpatterns.com/uploads/uMFTFbYZRVGHVRfhqnpaQp/pl-ob-1.png" alt="La Biblia de las Mezclas" className="hidden sm:block w-28 md:w-32 rounded-lg object-cover flex-shrink-0" loading="lazy" decoding="async" width={128} height={96} data-id="element-422" />
                  </div>

                  {/* Mobile image */}
                  <div className="sm:hidden mb-5" data-id="element-423">
                    <img src="https://cdn.magicpatterns.com/uploads/uMFTFbYZRVGHVRfhqnpaQp/pl-ob-1.png" alt="La Biblia de las Mezclas" className="w-full rounded-lg object-cover" loading="lazy" decoding="async" width={640} height={480} data-id="element-424" />
                  </div>

                  {/* Warning box */}
                  <div className="rounded-xl p-4 mb-5 text-sm leading-relaxed" style={{
                  background: 'rgba(232,85,85,0.08)',
                  border: '1px solid rgba(232,85,85,0.2)',
                  color: 'rgba(245,240,232,0.75)'
                }} data-id="element-425">
                    Existen combinaciones de ingredientes que parecen
                    inofensivas pero que en tu piel crean una reacción química
                    silenciosa que puede{' '}
                    <strong style={{
                    color: '#e88888'
                  }} data-id="element-426">
                      irritar tu barrera cutánea, manchar zonas sensibles y
                      anular completamente el efecto del Retinol.
                    </strong>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-5" data-id="element-427">
                    {[{
                    icon: <BanIcon className="w-4 h-4" data-id="element-428" />,
                    title: 'La Lista Negra Completa',
                    desc: 'Los 12 combos más peligrosos del skincare que probablemente tienes en tu baño ahora mismo.'
                  }, {
                    icon: <SparklesIcon className="w-4 h-4" data-id="element-429" />,
                    title: 'Las Mezclas de Oro',
                    desc: 'Las 8 combinaciones sinérgicas que MULTIPLICAN los resultados de tu protocolo al 300%.'
                  }, {
                    icon: <FlaskConicalIcon className="w-4 h-4" data-id="element-430" />,
                    title: 'El Método Sándwich',
                    desc: 'La técnica exacta para aplicar activos fuertes sin quemar tu piel.'
                  }, {
                    icon: <MicroscopeIcon className="w-4 h-4" data-id="element-431" />,
                    title: 'Guía de pH',
                    desc: 'Por qué el orden de aplicación importa más que los productos en sí mismos.'
                  }, {
                    icon: <ShieldCheckIcon className="w-4 h-4" data-id="element-432" />,
                    title: 'Protocolo de Rescate',
                    desc: 'Qué hacer si ya cometiste un error de mezcla. Recuperación en 48 horas.'
                  }].map((f, i) => <li key={i} className="flex gap-3 items-start text-sm" data-id="element-433">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{
                      background: 'rgba(201,169,110,0.15)',
                      border: '1px solid rgba(201,169,110,0.2)',
                      color: GOLD
                    }} data-id="element-434">
                          {f.icon}
                        </div>
                        <div data-id="element-435">
                          <strong style={{
                        color: CREAM
                      }} data-id="element-436">
                            {f.title}
                          </strong>
                          <span className="block text-xs mt-0.5" style={{
                        color: 'rgba(245,240,232,0.55)'
                      }} data-id="element-437">
                            {f.desc}
                          </span>
                        </div>
                      </li>)}
                  </ul>

                  {/* Closing quote */}
                  <div className="rounded-xl p-4 mb-5 text-sm italic leading-relaxed" style={{
                  background: '#1a1410',
                  border: `1px solid ${GLASS_BORDER}`,
                  color: 'rgba(245,240,232,0.7)'
                }} data-id="element-438">
                    "Sin esta guía, tienes el mapa de 28 días pero no las reglas
                    de seguridad del camino.{' '}
                    <strong className="not-italic" style={{
                    color: CREAM
                  }} data-id="element-439">
                      Es como tener un auto de lujo sin conocer las señales de
                      tráfico.
                    </strong>
                    "
                  </div>

                  {/* Footer: Price + CTA */}
                  <div className="rounded-xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4" style={{
                  background: '#1a1410',
                  border: `1px solid ${GLASS_BORDER}`
                }} data-id="element-440">
                    <div className="text-center sm:text-left" data-id="element-441">
                      <span className="text-xs line-through block" style={{
                      color: 'rgba(245,240,232,0.35)'
                    }} data-id="element-442">
                        Valor: $47.00 USD
                      </span>
                      <span className="text-3xl font-black" style={{
                      fontFamily: "'Playfair Display', serif",
                      background: `linear-gradient(135deg, ${GOLD_LIGHT}, ${GOLD})`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }} data-id="element-443">
                        $10.99
                      </span>
                      <span className="text-[11px] block" style={{
                      color: 'rgba(245,240,232,0.4)'
                    }} data-id="element-444">
                        USD · Pago único
                      </span>
                    </div>
                    <div className="flex-1 w-full sm:w-auto" data-id="element-445">
                      <button onClick={() => setBump1(!bump1)} className="w-full py-3 rounded-full font-bold text-sm tracking-wider transition-all hover:scale-[1.02]" style={{
                      background: bump1 ? 'rgba(201,169,110,0.3)' : `linear-gradient(135deg, ${GOLD_LIGHT}, ${GOLD}, ${GOLD_DARK})`,
                      color: bump1 ? GOLD_LIGHT : BG,
                      cursor: isMobile ? 'pointer' : 'none'
                    }} data-id="element-446">
                        {bump1 ? '✓ AGREGADO AL PEDIDO' : 'SÍ, AGREGAR AL PEDIDO'}
                      </button>
                      <p className="text-[10px] text-center mt-1.5 flex items-center justify-center gap-1" style={{
                      color: 'rgba(245,240,232,0.35)'
                    }} data-id="element-447">
                        <SmartphoneIcon className="w-3 h-3" data-id="element-448" /> PDF + Versión
                        Móvil · Acceso de por vida
                      </p>
                    </div>
                  </div>

                  {/* Trust */}
                  <div className="flex justify-center gap-4 mt-3 flex-wrap" data-id="element-449">
                    {[{
                    icon: <LockIcon className="w-3 h-3" data-id="element-450" />,
                    text: 'Pago Seguro'
                  }, {
                    icon: <ZapIcon className="w-3 h-3" data-id="element-451" />,
                    text: 'Acceso Inmediato'
                  }, {
                    icon: <SmartphoneIcon className="w-3 h-3" data-id="element-452" />,
                    text: 'PDF + Móvil'
                  }].map((t, i) => <span key={i} className="flex items-center gap-1 text-[10px]" style={{
                    color: 'rgba(245,240,232,0.35)'
                  }} data-id="element-453">
                        {t.icon} {t.text}
                      </span>)}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Divider */}
            <div className="max-w-2xl mx-auto flex items-center gap-4 mb-4" style={{
            opacity: 0.4
          }} data-id="element-454">
              <div className="flex-1 h-px" style={{
              background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`
            }} data-id="element-455" />
              <span className="text-[10px] tracking-wider uppercase whitespace-nowrap" style={{
              color: GOLD
            }} data-id="element-456">
                <CheckCircle2Icon className="w-3 h-3 inline mr-1" data-id="element-457" />
                Mejora adicional disponible
              </span>
              <div className="flex-1 h-px" style={{
              background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`
            }} data-id="element-458" />
            </div>

            {/* ─── ORDER BUMP #2 — SKIN-TRACKER PRO ─── */}
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="max-w-2xl mx-auto mb-6" data-id="element-459">
              <div className="rounded-2xl overflow-hidden transition-all duration-300" style={{
              background: '#13100f',
              boxShadow: bump2 ? '0 0 0 2px #4caf7d, 0 20px 60px rgba(76,175,125,0.2)' : 'none'
            }} data-id="element-460">
                {/* Ribbon */}
                <div className="px-5 py-2 flex items-center justify-between text-xs font-bold tracking-wider uppercase" style={{
                background: 'linear-gradient(90deg, #2d6b4a, #4caf7d, #2d6b4a)',
                color: '#fff'
              }} data-id="element-461">
                  <span className="flex items-center gap-1.5" data-id="element-462">
                    <TargetIcon className="w-3.5 h-3.5" data-id="element-463" /> ALTAMENTE RECOMENDADA
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-[10px]" style={{
                  background: 'rgba(0,0,0,0.2)'
                }} data-id="element-464">
                    NUEVO 2026
                  </span>
                </div>

                <div className="p-5 md:p-7" data-id="element-465">
                  {/* Top: Checkbox + Headline + Image */}
                  <div className="flex gap-4 items-start mb-5 pb-5 border-b" style={{
                  borderColor: GLASS_BORDER
                }} data-id="element-466">
                    <button onClick={() => setBump2(!bump2)} className="flex-shrink-0 mt-1 w-7 h-7 rounded-md border-2 flex items-center justify-center transition-all" style={{
                    borderColor: '#4caf7d',
                    background: bump2 ? '#4caf7d' : 'transparent',
                    cursor: isMobile ? 'pointer' : 'none'
                  }} data-id="element-467">
                      {bump2 && <CheckIcon className="w-4 h-4" style={{
                      color: '#fff'
                    }} data-id="element-468" />}
                    </button>
                    <div className="flex-1" data-id="element-469">
                      <p className="text-[11px] font-bold tracking-wider uppercase mb-1 flex items-center gap-1" style={{
                      color: '#4caf7d'
                    }} data-id="element-470">
                        <TargetIcon className="w-3 h-3" data-id="element-471" /> Completa tu
                        experiencia
                      </p>
                      <h3 className="text-base md:text-lg font-bold mb-2 leading-tight" style={{
                      fontFamily: "'Playfair Display', serif",
                      color: CREAM
                    }} data-id="element-472">
                        ¿Y si pudieras ver tu progreso día a día y{' '}
                        <span style={{
                        background: `linear-gradient(135deg, ${GOLD_LIGHT}, ${GOLD})`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }} data-id="element-473">
                          nunca olvidar en qué parte del ciclo estás?
                        </span>
                      </h3>
                      <p className="text-sm leading-relaxed" style={{
                      color: 'rgba(245,240,232,0.6)'
                    }} data-id="element-474">
                        Las mujeres que documentan su rutina tienen un 340% más
                        de probabilidad de completar el protocolo y ver
                        resultados reales.
                      </p>
                    </div>
                    <img src="https://cdn.magicpatterns.com/uploads/izwxzUohEMYHc9abMDuCrp/pl-ob-2.png" alt="Skin-Tracker Pro" className="hidden sm:block w-28 md:w-32 rounded-lg object-cover flex-shrink-0" loading="lazy" decoding="async" width={128} height={96} data-id="element-475" />
                  </div>

                  {/* Mobile image */}
                  <div className="sm:hidden mb-5" data-id="element-476">
                    <img src="https://cdn.magicpatterns.com/uploads/izwxzUohEMYHc9abMDuCrp/pl-ob-2.png" alt="Skin-Tracker Pro" className="w-full rounded-lg object-cover" loading="lazy" decoding="async" width={640} height={480} data-id="element-477" />
                  </div>

                  {/* Warning box */}
                  <div className="rounded-xl p-4 mb-5 text-sm leading-relaxed" style={{
                  background: 'rgba(76,175,125,0.08)',
                  border: '1px solid rgba(76,175,125,0.2)',
                  color: 'rgba(245,240,232,0.75)'
                }} data-id="element-478">
                    Empezar un protocolo con energía y abandonarlo en el día 8
                    porque{' '}
                    <strong style={{
                    color: '#7ddfb0'
                  }} data-id="element-479">
                      "se te olvidó en qué noche ibas"
                    </strong>{' '}
                    es el error número uno. El Skin-Cycling requiere
                    consistencia perfecta.
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-5" data-id="element-480">
                    {[{
                    icon: <CalendarIcon className="w-4 h-4" data-id="element-481" />,
                    title: 'Diario de 28 Días en Notion',
                    desc: 'Plantilla digital premium que te dice exactamente qué producto aplicar y en qué orden.'
                  }, {
                    icon: <CameraIcon className="w-4 h-4" data-id="element-482" />,
                    title: 'Tracker de Progreso Fotográfico',
                    desc: 'Sistema guiado para tomar fotos y comparar semana a semana.'
                  }, {
                    icon: <CheckSquareIcon className="w-4 h-4" data-id="element-483" />,
                    title: 'Checklist Diaria de 60 Segundos',
                    desc: '3 preguntas por noche para detectar si tu piel está respondiendo bien.'
                  }, {
                    icon: <FileTextIcon className="w-4 h-4" data-id="element-484" />,
                    title: 'Registro de Reacciones',
                    desc: 'Documenta cualquier irritación o cambio positivo con datos reales.'
                  }, {
                    icon: <PrinterIcon className="w-4 h-4" data-id="element-485" />,
                    title: 'Versión PDF Imprimible',
                    desc: 'Tracker de lujo para pegar en tu espejo de baño y tachar cada noche.'
                  }, {
                    icon: <BellIcon className="w-4 h-4" data-id="element-486" />,
                    title: 'Alertas de Ciclo',
                    desc: 'Instrucciones para configurar recordatorios sincronizados con tu ciclo de 4 noches.'
                  }].map((f, i) => <li key={i} className="flex gap-3 items-start text-sm" data-id="element-487">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{
                      background: 'rgba(76,175,125,0.15)',
                      border: '1px solid rgba(76,175,125,0.2)',
                      color: '#4caf7d'
                    }} data-id="element-488">
                          {f.icon}
                        </div>
                        <div data-id="element-489">
                          <strong style={{
                        color: CREAM
                      }} data-id="element-490">
                            {f.title}
                          </strong>
                          <span className="block text-xs mt-0.5" style={{
                        color: 'rgba(245,240,232,0.55)'
                      }} data-id="element-491">
                            {f.desc}
                          </span>
                        </div>
                      </li>)}
                  </ul>

                  {/* Closing quote */}
                  <div className="rounded-xl p-4 mb-5 text-sm italic leading-relaxed" style={{
                  background: '#1a1410',
                  border: `1px solid ${GLASS_BORDER}`,
                  color: 'rgba(245,240,232,0.7)'
                }} data-id="element-492">
                    "La diferencia entre las mujeres que ven resultados en 28
                    días y las que abandonan en la semana 2 no es el producto.{' '}
                    <strong className="not-italic" style={{
                    color: CREAM
                  }} data-id="element-493">
                      Es el sistema de seguimiento.
                    </strong>
                    "
                  </div>

                  {/* Footer: Price + CTA */}
                  <div className="rounded-xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4" style={{
                  background: '#1a1410',
                  border: `1px solid ${GLASS_BORDER}`
                }} data-id="element-494">
                    <div className="text-center sm:text-left" data-id="element-495">
                      <span className="text-xs line-through block" style={{
                      color: 'rgba(245,240,232,0.35)'
                    }} data-id="element-496">
                        Valor: $37.00 USD
                      </span>
                      <span className="text-3xl font-black" style={{
                      fontFamily: "'Playfair Display', serif",
                      background: 'linear-gradient(135deg, #7ddfb0, #4caf7d)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }} data-id="element-497">
                        $9.99
                      </span>
                      <span className="text-[11px] block" style={{
                      color: 'rgba(245,240,232,0.4)'
                    }} data-id="element-498">
                        USD · Pago único
                      </span>
                    </div>
                    <div className="flex-1 w-full sm:w-auto" data-id="element-499">
                      <button onClick={() => setBump2(!bump2)} className="w-full py-3 rounded-full font-bold text-sm tracking-wider transition-all hover:scale-[1.02]" style={{
                      background: bump2 ? 'rgba(76,175,125,0.3)' : 'linear-gradient(135deg, #7ddfb0, #4caf7d, #2d6b4a)',
                      color: bump2 ? '#7ddfb0' : '#fff',
                      cursor: isMobile ? 'pointer' : 'none'
                    }} data-id="element-500">
                        {bump2 ? '✓ AGREGADO AL PEDIDO' : 'SÍ, AGREGAR AL PEDIDO'}
                      </button>
                      <p className="text-[10px] text-center mt-1.5 flex items-center justify-center gap-1" style={{
                      color: 'rgba(245,240,232,0.35)'
                    }} data-id="element-501">
                        <SmartphoneIcon className="w-3 h-3" data-id="element-502" /> Notion + PDF ·
                        Compatible iPhone y Android
                      </p>
                    </div>
                  </div>

                  {/* Trust */}
                  <div className="flex justify-center gap-4 mt-3 flex-wrap" data-id="element-503">
                    {[{
                    icon: <LockIcon className="w-3 h-3" data-id="element-504" />,
                    text: 'Pago Seguro'
                  }, {
                    icon: <ZapIcon className="w-3 h-3" data-id="element-505" />,
                    text: 'Acceso Inmediato'
                  }, {
                    icon: <SmartphoneIcon className="w-3 h-3" data-id="element-506" />,
                    text: 'Notion + PDF'
                  }].map((t, i) => <span key={i} className="flex items-center gap-1 text-[10px]" style={{
                    color: 'rgba(245,240,232,0.35)'
                  }} data-id="element-507">
                        {t.icon} {t.text}
                      </span>)}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* ─── AOV SUMMARY BAR ─── */}
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="max-w-2xl mx-auto mb-12" data-id="element-508">
              <GlassCard data-id="element-509">
                <h3 className="text-sm font-semibold text-center tracking-wider mb-4" style={{
                fontFamily: "'Playfair Display', serif",
                color: GOLD
              }} data-id="element-510">
                  <CheckCircle2Icon className="w-4 h-4 inline mr-1" data-id="element-511" /> Resumen
                  de tu pedido
                </h3>
                <div className="space-y-2 mb-4" data-id="element-512">
                  <div className="flex justify-between items-center text-sm pb-2 border-b" style={{
                  borderColor: 'rgba(255,255,255,0.05)',
                  color: CREAM
                }} data-id="element-513">
                    <span className="flex items-center gap-2" data-id="element-514">
                      <BookOpenIcon className="w-4 h-4" style={{
                      color: GOLD
                    }} data-id="element-515" />
                      Protocolo de la Piel de Lujo (28 días)
                    </span>
                    <span className="font-semibold" data-id="element-516">$11.99</span>
                  </div>
                  <div className="flex justify-between items-center text-sm pb-2 border-b" style={{
                  borderColor: 'rgba(255,255,255,0.05)',
                  color: bump1 ? CREAM : 'rgba(245,240,232,0.4)'
                }} data-id="element-517">
                    <span className="flex items-center gap-2" data-id="element-518">
                      <span data-id="element-519">{bump1 ? '✓' : '○'}</span>
                      La Biblia de las Mezclas
                    </span>
                    <span className="font-semibold" style={{
                    color: bump1 ? CREAM : 'rgba(245,240,232,0.3)'
                  }} data-id="element-520">
                      $10.99
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm" style={{
                  color: bump2 ? CREAM : 'rgba(245,240,232,0.4)'
                }} data-id="element-521">
                    <span className="flex items-center gap-2" data-id="element-522">
                      <span data-id="element-523">{bump2 ? '✓' : '○'}</span>
                      Skin-Tracker Pro
                    </span>
                    <span className="font-semibold" style={{
                    color: bump2 ? CREAM : 'rgba(245,240,232,0.3)'
                  }} data-id="element-524">
                      $9.99
                    </span>
                  </div>
                </div>
                <div className="flex justify-between items-center pt-4 border-t" style={{
                borderColor: GLASS_BORDER
              }} data-id="element-525">
                  <span className="text-xs font-semibold uppercase tracking-wider" style={{
                  color: CREAM
                }} data-id="element-526">
                    Total hoy
                  </span>
                  <motion.span key={totalPrice} initial={{
                  scale: 1.15
                }} animate={{
                  scale: 1
                }} className="text-3xl font-black" style={{
                  fontFamily: "'Playfair Display', serif",
                  background: `linear-gradient(135deg, ${GOLD_LIGHT}, ${GOLD})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }} data-id="element-527">
                    ${totalPrice.toFixed(2)}
                  </motion.span>
                </div>

                <button onClick={handleCheckout} className="w-full mt-5 py-4 text-base font-semibold tracking-wider flex items-center justify-center gap-2 transition-all hover:scale-[1.02]" style={{
                background: GOLD,
                color: BG,
                cursor: isMobile ? 'pointer' : 'none'
              }} data-id="element-528">
                  COMPLETAR PEDIDO — ${totalPrice.toFixed(2)}
                  <ArrowRightIcon className="w-5 h-5" data-id="element-529" />
                </button>
                <div className="flex items-center justify-center gap-4 mt-3 text-xs" style={{
                color: 'rgba(245,240,232,0.4)'
              }} data-id="element-530">
                  <span className="flex items-center gap-1" data-id="element-531">
                    <LockIcon className="w-3 h-3" data-id="element-532" /> Pago seguro
                  </span>
                  <span className="flex items-center gap-1" data-id="element-533">
                    <ShieldCheckIcon className="w-3 h-3" data-id="element-534" /> Garantía 7 días
                  </span>
                  <span className="flex items-center gap-1" data-id="element-535">
                    <ZapIcon className="w-3 h-3" data-id="element-536" /> Acceso inmediato
                  </span>
                </div>
              </GlassCard>
            </motion.div>
          </motion.div>

          {/* Bonus Mockup Cards */}
          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-537">
            {BONUSES.map((b, i) => <motion.div key={i} variants={slideInRight} data-id="element-538">
                <GlassCard className="h-full" data-id="element-539">
                  {b.image ? <div className="rounded-lg overflow-hidden mb-4 aspect-[4/3]" data-id="element-540">
                      <img src={b.image} alt={b.title} className="w-full h-full object-cover" loading="lazy" decoding="async" width={400} height={300} data-id="element-541" />
                    </div> : <div className="rounded-lg mb-4 aspect-[4/3] flex items-center justify-center" style={{
                background: 'rgba(201,169,110,0.08)'
              }} data-id="element-542">
                      <BookOpenIcon className="w-12 h-12" style={{
                  color: GOLD,
                  opacity: 0.4
                }} data-id="element-543" />
                    </div>}
                  <div className="flex items-center gap-2 mb-2" data-id="element-544">
                    {b.icon}
                    <h3 className="text-sm font-bold" style={{
                  fontFamily: "'Playfair Display', serif",
                  color: CREAM
                }} data-id="element-545">
                      {b.title}
                    </h3>
                  </div>
                  <p className="text-xs leading-relaxed" style={{
                color: 'rgba(245,240,232,0.5)'
              }} data-id="element-546">
                    {b.desc}
                  </p>
                </GlassCard>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ─── TESTIMONIOS ─── */}
      <section className="relative z-10 py-16 md:py-24 px-4" data-id="element-547">
        <div className="max-w-5xl mx-auto" data-id="element-548">
          <SectionLabel icon={<StarIcon className="w-4 h-4" data-id="element-550" />} text="Testimonios" data-id="element-549" />
          <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="text-3xl md:text-5xl font-bold mb-12" style={{
          fontFamily: "'Playfair Display', serif"
        }} data-id="element-551">
            Lo que dicen nuestras{' '}
            <span style={{
            color: GOLD
          }} data-id="element-552">
              clientas
            </span>
          </motion.h2>

          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-553">
            {TESTIMONIALS.map((t, i) => <motion.div key={i} variants={fadeUp} data-id="element-554">
                <GlassCard className="h-full" data-id="element-555">
                  <div className="flex gap-1 mb-4" data-id="element-556">
                    {[...Array(5)].map((_, j) => <StarIcon key={j} className="w-4 h-4 fill-current" style={{
                  color: GOLD
                }} data-id="element-557" />)}
                  </div>
                  <p className="text-sm leading-relaxed mb-6 italic" style={{
                color: 'rgba(245,240,232,0.75)'
              }} data-id="element-558">
                    "{t.quote}"
                  </p>
                  <div className="flex items-center gap-3" data-id="element-559">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold" style={{
                  background: 'rgba(201,169,110,0.15)',
                  color: GOLD
                }} data-id="element-560">
                      {t.initials}
                    </div>
                    <div data-id="element-561">
                      <p className="text-sm font-semibold" style={{
                    color: CREAM
                  }} data-id="element-562">
                        {t.name}
                      </p>
                      <p className="text-xs" style={{
                    color: 'rgba(245,240,232,0.4)'
                  }} data-id="element-563">
                        {t.city}
                      </p>
                    </div>
                  </div>
                </GlassCard>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ─── GARANTÍA ─── */}
      <section className="relative z-10 py-16 md:py-24 px-4" data-id="element-564">
        <div className="max-w-3xl mx-auto text-center" data-id="element-565">
          <SectionLabel icon={<ShieldCheckIcon className="w-4 h-4" data-id="element-567" />} text="Sin Riesgo" data-id="element-566" />

          {/* Rotating Seal */}
          <div className="relative w-32 h-32 mx-auto mb-8" data-id="element-568">
            <svg className="lujo-seal w-full h-full" viewBox="0 0 120 120" data-id="element-569">
              <defs data-id="element-570">
                <path id="sealCircle" d="M 60,60 m -45,0 a 45,45 0 1,1 90,0 a 45,45 0 1,1 -90,0" data-id="element-571" />
              </defs>
              <text fill={GOLD} fontSize="9" fontWeight="600" letterSpacing="3" data-id="element-572">
                <textPath href="#sealCircle" data-id="element-573">
                  GARANTÍA DE SATISFACCIÓN ★ 7 DÍAS ★ 100% REEMBOLSO ★
                </textPath>
              </text>
            </svg>
            <div className="absolute inset-0 flex items-center justify-center" data-id="element-574">
              <div className="text-center" data-id="element-575">
                <span className="text-3xl font-bold block" style={{
                fontFamily: "'Playfair Display', serif",
                color: GOLD
              }} data-id="element-576">
                  7
                </span>
                <span className="text-[10px] tracking-wider uppercase" style={{
                color: GOLD_LIGHT
              }} data-id="element-577">
                  días
                </span>
              </div>
            </div>
          </div>

          <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="text-2xl md:text-4xl font-bold mb-4" style={{
          fontFamily: "'Playfair Display', serif"
        }} data-id="element-578">
            Garantía incondicional de{' '}
            <span style={{
            color: GOLD
          }} data-id="element-579">
              7 días
            </span>
          </motion.h2>
          <motion.p variants={fadeUp} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="text-base leading-relaxed max-w-xl mx-auto" style={{
          color: 'rgba(245,240,232,0.6)'
        }} data-id="element-580">
            Si el protocolo no transforma tu piel en los primeros 7 días, te
            devolvemos cada centavo. Sin preguntas, sin complicaciones. El
            riesgo es completamente nuestro.
          </motion.p>
        </div>
      </section>

      {/* ─── CTA FINAL ─── */}
      <section className="relative z-10 py-16 md:py-24 px-4" data-id="element-581">
        <div className="max-w-3xl mx-auto text-center" data-id="element-582">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} data-id="element-583">
            <h2 className="text-3xl md:text-5xl font-bold mb-6" style={{
            fontFamily: "'Playfair Display', serif"
          }} data-id="element-584">
              Tu piel merece{' '}
              <span style={{
              background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }} data-id="element-585">
                ciencia, no marketing
              </span>
            </h2>
            <p className="text-base mb-10 max-w-xl mx-auto" style={{
            color: 'rgba(245,240,232,0.6)'
          }} data-id="element-586">
              Deja de pagar de más por envases bonitos. Empieza hoy con el
              protocolo que realmente funciona.
            </p>
            <button onClick={scrollToOffer} className="px-10 py-5 text-lg font-semibold tracking-wider inline-flex items-center gap-3 transition-all hover:scale-105" style={{
            background: GOLD,
            color: BG,
            cursor: isMobile ? 'pointer' : 'none'
          }} data-id="element-587">
              COMENZAR MI TRANSFORMACIÓN
              <ArrowRightIcon className="w-5 h-5" data-id="element-588" />
            </button>
            <div className="flex items-center justify-center gap-6 mt-6 text-xs" style={{
            color: 'rgba(245,240,232,0.4)'
          }} data-id="element-589">
              <span className="flex items-center gap-1" data-id="element-590">
                <LockIcon className="w-3 h-3" data-id="element-591" /> Pago 100% seguro
              </span>
              <span className="flex items-center gap-1" data-id="element-592">
                <ShieldCheckIcon className="w-3 h-3" data-id="element-593" /> Garantía 7 días
              </span>
              <span className="flex items-center gap-1" data-id="element-594">
                <ZapIcon className="w-3 h-3" data-id="element-595" /> Descarga inmediata
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="relative z-10 border-t py-8 px-4" style={{
      borderColor: GLASS_BORDER
    }} data-id="element-596">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-597">
          <span className="text-sm font-bold tracking-wider" style={{
          fontFamily: "'Playfair Display', serif",
          color: GOLD
        }} data-id="element-598">
            PIEL DE LUJO
          </span>
          <p className="text-xs" style={{
          color: 'rgba(245,240,232,0.3)'
        }} data-id="element-599">
            © 2026 Rutina Simple. Todos los derechos reservados.
          </p>
          <div className="flex gap-4 text-xs" style={{
          color: 'rgba(245,240,232,0.4)'
        }} data-id="element-600">
            <span data-id="element-601">Términos</span>
            <span data-id="element-602">Privacidad</span>
            <span data-id="element-603">Contacto</span>
          </div>
        </div>
      </footer>

      {/* Social Proof Toasts */}
      <RecentPurchaseToast data-id="element-604" />

      {/* ─── EXIT-INTENT UPSELL POPUP ─── */}
      <AnimatePresence data-id="element-605">
        {showUpsell && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{
        background: 'rgba(0,0,0,0.8)',
        backdropFilter: 'blur(8px)'
      }} onClick={() => setShowUpsell(false)} data-id="element-606">
            <motion.div initial={{
          scale: 0.9,
          y: 30,
          opacity: 0
        }} animate={{
          scale: 1,
          y: 0,
          opacity: 1
        }} exit={{
          scale: 0.9,
          y: 30,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 350,
          damping: 30
        }} className="relative w-full max-w-lg rounded-2xl overflow-hidden" style={{
          background: '#13100f',
          border: `1px solid ${GLASS_BORDER}`,
          cursor: 'auto'
        }} onClick={e => e.stopPropagation()} data-id="element-607">
              {/* Close button */}
              <button onClick={() => setShowUpsell(false)} className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full flex items-center justify-center transition-opacity hover:opacity-80" style={{
            background: 'rgba(255,255,255,0.1)',
            color: CREAM,
            cursor: isMobile ? 'pointer' : 'none'
          }} data-id="element-608">
                <XIcon className="w-4 h-4" data-id="element-609" />
              </button>

              {/* Header Banner */}
              <div className="px-6 py-4 text-center" style={{
            background: `linear-gradient(135deg, ${GOLD_DARK}, ${GOLD}, ${GOLD_DARK})`
          }} data-id="element-610">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase mb-2" style={{
              background: 'rgba(0,0,0,0.25)',
              color: '#fff'
            }} data-id="element-611">
                  <ZapIcon className="w-3 h-3" data-id="element-612" /> Solo disponible en esta
                  pantalla
                </div>
                <h3 className="text-lg md:text-xl font-bold" style={{
              fontFamily: "'Playfair Display', serif",
              color: BG
            }} data-id="element-613">
                  ¡Espera! No te vayas sin esto
                </h3>
                <p className="text-xs mt-1" style={{
              color: 'rgba(10,6,8,0.7)'
            }} data-id="element-614">
                  Una oferta exclusiva que no volverás a ver
                </p>
              </div>

              <div className="px-6 py-5" data-id="element-615">
                {/* Flashing discount badge */}
                <div className="flex justify-center mb-4" data-id="element-616">
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold tracking-wider" style={{
                background: 'rgba(232,85,85,0.15)',
                border: '1px solid rgba(232,85,85,0.3)',
                color: '#e88888',
                animation: 'lujo-pulse 1.5s ease-in-out infinite'
              }} data-id="element-617">
                    <SparklesIcon className="w-4 h-4" data-id="element-618" /> 30% OFF EN EXTRAS
                  </span>
                </div>

                {/* Main copy */}
                <p className="text-sm text-center leading-relaxed mb-4" style={{
              color: 'rgba(245,240,232,0.75)'
            }} data-id="element-619">
                  Lleva el{' '}
                  <strong style={{
                color: CREAM
              }} data-id="element-620">
                    Paquete Completo
                  </strong>{' '}
                  con el protocolo + las dos herramientas que triplican tus
                  resultados. Las mujeres que usan el sistema completo ven
                  resultados{' '}
                  <strong style={{
                color: GOLD
              }} data-id="element-621">
                    3x más rápido
                  </strong>
                  .
                </p>

                {/* What's included */}
                <div className="rounded-xl p-4 mb-4 space-y-2.5" style={{
              background: 'rgba(255,255,255,0.03)',
              border: `1px solid ${GLASS_BORDER}`
            }} data-id="element-622">
                  <p className="text-xs font-bold tracking-wider uppercase mb-2" style={{
                color: GOLD
              }} data-id="element-623">
                    Tu paquete incluye:
                  </p>
                  {[{
                name: 'El Protocolo de la Piel de Lujo',
                detail: 'Sistema completo de 28 días',
                price: '$11.99'
              }, {
                name: 'La Biblia de las Mezclas',
                detail: 'Guía de combinaciones seguras',
                price: '$10.99'
              }, {
                name: 'Skin-Tracker Pro',
                detail: 'Diario + tracker fotográfico',
                price: '$9.99'
              }].map((item, i) => <div key={i} className="flex items-center justify-between text-sm" data-id="element-624">
                      <span className="flex items-center gap-2" style={{
                  color: CREAM
                }} data-id="element-625">
                        <CheckCircle2Icon className="w-4 h-4 flex-shrink-0" style={{
                    color: GOLD
                  }} data-id="element-626" />
                        <span data-id="element-627">
                          <strong data-id="element-628">{item.name}</strong>
                          <span className="text-xs ml-1" style={{
                      color: 'rgba(245,240,232,0.5)'
                    }} data-id="element-629">
                            — {item.detail}
                          </span>
                        </span>
                      </span>
                      <span className="text-xs flex-shrink-0 ml-2" style={{
                  color: 'rgba(245,240,232,0.4)'
                }} data-id="element-630">
                        {item.price}
                      </span>
                    </div>)}
                </div>

                {/* Pricing block */}
                <div className="text-center mb-5" data-id="element-631">
                  <div className="flex items-center justify-center gap-3 mb-1" data-id="element-632">
                    <span className="text-lg line-through" style={{
                  color: 'rgba(245,240,232,0.3)'
                }} data-id="element-633">
                      $32.97
                    </span>
                    <span className="text-4xl font-black" style={{
                  fontFamily: "'Playfair Display', serif",
                  background: `linear-gradient(135deg, ${GOLD_LIGHT}, ${GOLD})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }} data-id="element-634">
                      $26.68
                    </span>
                  </div>
                  <div className="flex items-center justify-center gap-3 text-[10px]" style={{
                color: 'rgba(245,240,232,0.4)'
              }} data-id="element-635">
                    <span className="flex items-center gap-1" data-id="element-636">
                      <LockIcon className="w-3 h-3" data-id="element-637" /> Pago seguro
                    </span>
                    <span data-id="element-638">Precio exclusivo de esta pantalla</span>
                  </div>
                </div>

                {/* CTA Buttons */}
                <button onClick={() => {
              setShowUpsell(false);
              trackAddToCart();
              window.open(FULL_BUNDLE_URL, '_blank');
            }} className="w-full py-4 rounded-lg text-sm font-bold tracking-wider flex items-center justify-center gap-2 transition-all hover:scale-[1.02] mb-3" style={{
              background: `linear-gradient(135deg, ${GOLD_LIGHT}, ${GOLD}, ${GOLD_DARK})`,
              color: BG,
              cursor: isMobile ? 'pointer' : 'none',
              boxShadow: `0 0 20px rgba(201,169,110,0.3)`,
              animation: 'lujo-pulse 2.5s ease-in-out infinite'
            }} data-id="element-639">
                  SÍ, QUIERO EL PAQUETE COMPLETO — $26.68
                  <ArrowRightIcon className="w-4 h-4" data-id="element-640" />
                </button>

                <button onClick={() => {
              setShowUpsell(false);
              window.open(BASE_ONLY_URL, '_blank');
            }} className="w-full py-2.5 text-xs transition-opacity hover:opacity-80 text-center" style={{
              color: 'rgba(245,240,232,0.35)',
              background: 'transparent',
              border: 'none',
              cursor: isMobile ? 'pointer' : 'none'
            }} data-id="element-641">
                  No gracias, continuar sin los extras →
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}